import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type MessagingProviderType = 'ELEFA' | 'TWILIO_CONVERSATIONS';

/**
 * Client-side hook to fetch the active messaging provider.
 * Used by chat components to transparently route through Elefa E2EE or Twilio Conversations.
 */
export function useActiveMessagingProvider() {
  const { data: provider, isLoading, isFetched, isSuccess, isError, error } = useQuery({
    queryKey: ['active-messaging-provider'],
    queryFn: async () => {
      // Guard: only fetch when user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.log('[ActiveMessagingProvider] No auth session, defaulting to ELEFA');
        return 'ELEFA' as MessagingProviderType;
      }

      console.log('[ActiveMessagingProvider] Fetching...');
      const { data, error } = await supabase.functions.invoke('get-active-messaging-provider');

      if (error) {
        console.warn('[ActiveMessagingProvider] Error (non-fatal, defaulting to ELEFA):', error);
        return 'ELEFA' as MessagingProviderType;
      }

      const result = data as { provider?: MessagingProviderType } | null;
      const p = result?.provider || 'ELEFA';
      console.log('[ActiveMessagingProvider] Active:', p);
      return p;
    },
    staleTime: 10_000,
    gcTime: 60_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  return {
    provider: provider ?? 'ELEFA',
    isLoading,
    isReady: isFetched && !isLoading,
    isError,
    error: isError ? error : null,
  };
}
